import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalSettingsService } from '../globalSettings.service';
import { ModalIcon, ModalKeys, NotificationService } from '@shared/notification/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { EupRoutesService } from '@core/eupRoutes.service';
import { CountryCode, FeatureToggle, Regions } from '@shared/enums';
import { FeaturesToggleSettingsService } from '../../featuresToggleSettings/service/featuresToggleSettings.service';
import { forwardQueryParamsOnRegionRedirect, loginPath } from 'assets/consts';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import * as QueryString from "querystring";
import { PlatformLoginService } from 'app/services/platform-login/platform-login.service';
import { TimberService } from '@logging/timber.service';

@Injectable()
export class RegionGuard  {
    constructor(
        private globalSettings: GlobalSettingsService,
        private notificationService: NotificationService,
        private eupRoutesService: EupRoutesService,
        private translateService: TranslateService,
        private router: Router,
        private featuresToggleSettingsService: FeaturesToggleSettingsService,
		private activatedRoute: ActivatedRoute,
        private platformLoginService: PlatformLoginService,
        private timberService: TimberService
	) { }

    canActivate(): Observable<boolean> {
        return this.featuresToggleSettingsService.getFeaturesToggleSettings().pipe(
            // Split-endpoint migration: this guard reads the secured field MIDCUrlForRegionRedirect.
            // Wait until secured routing data is available (no-op when the flag is off) before deciding.
            switchMap((toggles) => this.eupRoutesService.whenSecuredRoutesReady().pipe(map(() => toggles))),
            map(() => {
                const isRedirectionCNVisible = this.featuresToggleSettingsService.isVisibleFeature(FeatureToggle.RedirectionCN);
                this.log('[RegionGuard] RedirectionCN feature toggle', { isRedirectionCNVisible: isRedirectionCNVisible.toString() });
                if (!isRedirectionCNVisible) {
                    this.log('[RegionGuard] RedirectionCN not visible, allowing activation');
                    return true;
                }
                const redirectStatus = this.checkRegion();
                this.log('[RegionGuard] checkRegion result', { redirectStatus });
				
                switch (redirectStatus) {
                    case 'redirectToCN':
                        this.log('[RegionGuard] Showing redirect to CN notification');
                        this.notificationService.show(
                            this.translateService.instant('RegionRedirect.RedirectHeader'),
                            this.translateService.instant('RegionRedirect.RedirectToCNMessage'),
                            { buttonCombination: ModalKeys.Ok, icon: ModalIcon.Info },
                            this.translateService.instant('RegionRedirect.ConfirmRedirectToCN')
                        ).subscribe(() => {
                            // redirect to CN region URL
                            this.log('[RegionGuard] User confirmed redirect to CN, redirecting', { redirectUrl: this.eupRoutesService.MIDCUrlForRegionRedirect });
                            this.redirectToCorrectRegion(this.eupRoutesService.MIDCUrlForRegionRedirect);
                        });
                        break;
                    case 'redirectToCom':
                        this.log('[RegionGuard] Showing redirect to Com notification');
                        this.notificationService.show(
                            this.translateService.instant('RegionRedirect.RedirectHeader'),
                            this.translateService.instant('RegionRedirect.RedirectToComMessage'),
                            { buttonCombination: ModalKeys.Ok, icon: ModalIcon.Info },
                            this.translateService.instant('RegionRedirect.ConfirmRedirectToCom')
                        ).subscribe(() => {
                            // redirect to com region URL
                            this.log('[RegionGuard] Redirect to Com, redirecting', { redirectUrl: this.eupRoutesService.MIDCUrlForRegionRedirect });
                            this.redirectToCorrectRegion(this.eupRoutesService.MIDCUrlForRegionRedirect);
                        });
                        break;
                    case 'unable-to-login':
                        this.log('[RegionGuard] Showing unable to login notification');
                        this.notificationService.show(
                            this.translateService.instant('RegionRedirect.UnableToLoginHeader'),
                            this.translateService.instant('RegionRedirect.UnableToLoginMessage'),
                            { buttonCombination: ModalKeys.Ok, icon: ModalIcon.Info }
                        ).subscribe(() => {
                            // back to login
                            this.log('[RegionGuard] User confirmed unable to login, navigating to login');
                            this.router.navigate([ loginPath ]);
                        });
                        break;
                    case 'login': 
                        this.log('[RegionGuard] Login case, allowing activation');
                        return true;
                }
                this.log('[RegionGuard] Blocking activation (no matching case or redirecting)');
                return false;
            })
        );
    }

    private redirectToCorrectRegion = (origin: string) => {
		const forwardedQueryString = this.getForwardedQueryString();
        this.log('[RegionGuard] redirectToCorrectRegion', { origin, forwardedQueryString });
        this.platformLoginService.navigateToExternalLogin(origin, forwardedQueryString);
	}

	private getForwardedQueryString(): string {
		const forwardParams = {};
		const queryParams = this.activatedRoute.snapshot.queryParams;
        this.log('[RegionGuard] getForwardedQueryString - queryParams', { queryParams: JSON.stringify(queryParams) });
		for (let paramToForward of forwardQueryParamsOnRegionRedirect) {
			if (Object.keys(queryParams).includes(paramToForward)) {
				forwardParams[paramToForward] = queryParams[paramToForward];
			}
		}

		if (Object.keys(forwardParams).length) {
            const queryString = QueryString.stringify(forwardParams);
            this.log('[RegionGuard] getForwardedQueryString - forwarding params', { forwardParams: JSON.stringify(forwardParams), queryString });
			return queryString;
		}
		
        this.log('[RegionGuard] getForwardedQueryString - no params to forward');
		return '';
	}

    private checkRegion = (): 'unable-to-login' | 'redirectToCN' | 'redirectToCom' | 'login' => {
        this.log('[RegionGuard] checkRegion called');
        const isChinaRegion = this.eupRoutesService.Region === Regions.china;
        this.log('[RegionGuard] isChinaRegion', { isChinaRegion: isChinaRegion.toString(), currentRegion: this.eupRoutesService.Region });
        const glbSettings = this.globalSettings.get();
        this.log('[RegionGuard] globalSettings companies count', { companiesCount: glbSettings.companies.length.toString() });
        const chineseCompaniesCount = glbSettings.companies.filter(setting => setting.countryCode === CountryCode.China).length;
        this.log('[RegionGuard] chineseCompaniesCount', { chineseCompaniesCount: chineseCompaniesCount.toString() });
        const hasChineseCompanies = {
            all: chineseCompaniesCount === glbSettings.companies.length,
            none: chineseCompaniesCount === 0,
            some: chineseCompaniesCount > 0 && chineseCompaniesCount < glbSettings.companies.length
        };
        this.log('[RegionGuard] hasChineseCompanies', { hasAll: hasChineseCompanies.all.toString(), hasNone: hasChineseCompanies.none.toString(), hasSome: hasChineseCompanies.some.toString() });
        if (hasChineseCompanies.some) {
            this.log('[RegionGuard] User has some Chinese companies - unable to login');
            return 'unable-to-login';
        }
        if (isChinaRegion && hasChineseCompanies.none) {
            this.log('[RegionGuard] In China region but no Chinese companies - redirect to Com');
            return 'redirectToCom';
        }
        if (!isChinaRegion && hasChineseCompanies.all) {
            const isWso2Enabled = this.featuresToggleSettingsService.isVisibleFeature(FeatureToggle.EnableWso2Authentication, false);
            this.log('[RegionGuard] Not in China region but all companies are Chinese', { isWso2Enabled: isWso2Enabled.toString() });
            if (isWso2Enabled) {
                this.log('[RegionGuard] Wso2Authentication enabled - allowing login');
                return 'login';
            }
            this.log('[RegionGuard] Wso2Authentication disabled - redirect to CN');
            return 'redirectToCN';
        }
        this.log('[RegionGuard] Default case - allowing login');
        return 'login';
    }

    private log(message: string, extendedParameters?: Record<string, string>): void {
        const context = extendedParameters
            ? { module: 'RegionGuard', extendedParameters }
            : { module: 'RegionGuard' };
        this.timberService.info(message, context);
    }
}
